// Imagem do ingresso/convite no padrão Se Liga (1080×1920, para abrir na tela do celular).
// Logo em cima, evento, selo, nome, QR grande, "Mostre esta tela na entrada" e o rodapé azul
// com o site. Mesmo desenho das cortesias do Luau (30/09/2026).
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';

export const LOGO_CONVITE =
  'https://tzzdgiacrttvmklooxek.supabase.co/storage/v1/object/public/icons/logo-convite.png';

export interface DadosConvite {
  evento: string;
  quando?: string | null; // ex.: "Sábado, 10/10 · 19h30"
  local?: string | null;
  rotulo?: string; // selo amarelo: "INGRESSO", "INSCRIÇÃO", "CORTESIA"…
  nome: string; // quem entra
  detalhe?: string | null; // linha cinza abaixo do nome (participação, CPF mascarado…)
  codigo: string; // EXATAMENTE o valor do QR que aparece na tela
}

const L = 1080;
const A = 1920;
const AZUL = '#1E46D6';
const AZUL_ESCURO = '#102478';
const AMARELO = '#FFC41A';
const CINZA = '#5A606E';
const PRETO = '#14141C';

function carregarLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // sem isso a imagem "suja" o canvas e o download falha
    const t = setTimeout(() => resolve(null), 4000);
    img.onload = () => {
      clearTimeout(t);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(t);
      resolve(null);
    };
    img.src = LOGO_CONVITE;
  });
}

// Desenha o QR a partir do SVG do próprio qrcode.react (quadrados exatos, sem borrão).
function desenharQr(ctx: CanvasRenderingContext2D, valor: string, x: number, y: number, lado: number) {
  const div = document.createElement('div');
  const root = createRoot(div);
  flushSync(() => {
    root.render(createElement(QRCodeSVG, { value: valor, level: 'M', marginSize: 4 }));
  });
  const svg = div.querySelector('svg');
  const caminhos = Array.from(div.querySelectorAll('path'));
  const n = Number(svg?.getAttribute('viewBox')?.split(' ')[2] ?? 0);
  root.unmount();
  if (!n || caminhos.length === 0) throw new Error('Não foi possível gerar o QR.');
  const modulo = Math.floor(lado / n);
  const real = modulo * n;
  const ox = x + Math.floor((lado - real) / 2);
  const oy = y + Math.floor((lado - real) / 2);
  ctx.save();
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(ox, oy, real, real);
  ctx.fillStyle = '#000000';
  // o caminho do qrcode.react é uma lista de faixas "M x y h largura v1 H x z" (uma por trecho escuro)
  const d = caminhos[caminhos.length - 1].getAttribute('d') ?? '';
  const faixa = /M(\d+)[ ,](\d+)\s?h(\d+)v1H\d+z/g;
  let m: RegExpExecArray | null;
  let desenhadas = 0;
  while ((m = faixa.exec(d)) !== null) {
    ctx.fillRect(ox + Number(m[1]) * modulo, oy + Number(m[2]) * modulo, Number(m[3]) * modulo, modulo);
    desenhadas += 1;
  }
  ctx.restore();
  if (desenhadas === 0) throw new Error('Não foi possível gerar o QR.');
}

// retângulo de pontas redondas (sem roundRect, que falta em iPhone antigo)
function pilula(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  const r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
}

function caber(ctx: CanvasRenderingContext2D, texto: string, peso: string, tamanho: number, minimo: number,
               familia: string, largura: number): number {
  let t = tamanho;
  ctx.font = `${peso} ${t}px ${familia}`;
  while (ctx.measureText(texto).width > largura && t > minimo) {
    t -= 2;
    ctx.font = `${peso} ${t}px ${familia}`;
  }
  return t;
}

// Quebra em até 2 linhas quando não cabe nem no tamanho mínimo.
function linhas(ctx: CanvasRenderingContext2D, texto: string, largura: number): string[] {
  if (ctx.measureText(texto).width <= largura) return [texto];
  const palavras = texto.split(/\s+/);
  let a = '';
  let i = 0;
  while (i < palavras.length && ctx.measureText((a ? a + ' ' : '') + palavras[i]).width <= largura) {
    a = (a ? a + ' ' : '') + palavras[i];
    i += 1;
  }
  let b = palavras.slice(i).join(' ');
  while (b && ctx.measureText(b + '…').width > largura) b = b.slice(0, -1);
  if (i < palavras.length && b !== palavras.slice(i).join(' ')) b = b.trimEnd() + '…';
  return a ? [a, b].filter(Boolean) : [texto.slice(0, 30) + '…'];
}

export async function gerarConvite(d: DadosConvite): Promise<HTMLCanvasElement> {
  try {
    await document.fonts.ready;
  } catch {
    /* segue com a fonte que tiver */
  }
  const familia = getComputedStyle(document.body).fontFamily || 'sans-serif';
  const logo = await carregarLogo();

  const canvas = document.createElement('canvas');
  canvas.width = L;
  canvas.height = A;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Seu navegador não conseguiu gerar a imagem.');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, L, A);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const meio = L / 2;
  const util = L - 100;
  let y = 40;

  // 1 · logo (ou o nome escrito, se a imagem não carregar)
  if (logo) {
    const lw = 280;
    const lh = Math.round((logo.height * lw) / logo.width);
    ctx.drawImage(logo, (L - lw) / 2, y, lw, lh);
    y += lh + 4;
  } else {
    ctx.fillStyle = AZUL;
    ctx.font = `800 64px ${familia}`;
    ctx.fillText('Se Liga Católico', meio, y + 20);
    y += 110;
  }

  // 2 · evento (até 2 linhas)
  ctx.fillStyle = AZUL_ESCURO;
  const te = caber(ctx, d.evento.toUpperCase(), '800', 84, 56, familia, util);
  for (const l of linhas(ctx, d.evento.toUpperCase(), util)) {
    ctx.fillText(l, meio, y);
    y += te + 18;
  }

  // 3 · quando e onde
  ctx.fillStyle = CINZA;
  const quandoOnde = [d.quando, d.local].filter(Boolean).join(' · ');
  if (quandoOnde) {
    const tq = caber(ctx, quandoOnde, '500', 34, 26, familia, util);
    for (const l of linhas(ctx, quandoOnde, util)) {
      ctx.fillText(l, meio, y);
      y += tq + 12;
    }
  }
  y += 16;

  // 4 · selo amarelo
  const rotulo = (d.rotulo || 'INGRESSO').toUpperCase();
  ctx.font = `800 58px ${familia}`;
  const lr = ctx.measureText(rotulo).width;
  ctx.fillStyle = AMARELO;
  const x0 = meio - lr / 2 - 44;
  pilula(ctx, x0, y, lr + 88, 88);
  ctx.fillStyle = AZUL_ESCURO;
  ctx.fillText(rotulo, meio, y + 12);
  y += 108;

  // 5 · nome e detalhe
  ctx.fillStyle = PRETO;
  const tn = caber(ctx, d.nome, '800', 82, 50, familia, util);
  for (const l of linhas(ctx, d.nome, util)) {
    ctx.fillText(l, meio, y);
    y += tn + 12;
  }
  y += 10;
  if (d.detalhe) {
    ctx.fillStyle = CINZA;
    caber(ctx, d.detalhe, '500', 40, 28, familia, util);
    ctx.fillText(linhas(ctx, d.detalhe, util)[0], meio, y - 8);
    y += 50;
  }

  // 6 · QR (o maior que couber, até 800 px)
  const rodape = 120;
  const textoBaixo = 130;
  const lado = Math.max(560, Math.min(800, A - rodape - textoBaixo - y - 10));
  desenharQr(ctx, d.codigo, (L - lado) / 2, y, lado);
  y += lado + 10;

  // 7 · instruções
  ctx.fillStyle = AZUL_ESCURO;
  ctx.font = `800 46px ${familia}`;
  ctx.fillText('Mostre esta tela na entrada', meio, y);
  ctx.fillStyle = CINZA;
  ctx.font = `400 32px ${familia}`;
  ctx.fillText('Deixe o brilho do celular no máximo', meio, y + 62);

  // 8 · rodapé azul (divulgação)
  ctx.fillStyle = AZUL;
  ctx.fillRect(0, A - rodape, L, rodape);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `800 52px ${familia}`;
  ctx.fillText('seligacatolico.com.br', meio, A - rodape + 26);

  return canvas;
}